import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, Plug } from 'lucide-react';
import { TaskySpinner } from '../tasky/feedback/TaskyLoading';
import { TaapInitialSync, TaapSyncStatus } from '../../types/taap';
import { formatRelativeTime, PLATFORM_LABELS } from '../../utils/taapFormat';

interface Props {
  status: TaapSyncStatus | null;
  loading: boolean;
  onConnect: () => void;
}

const formatScheduledTime = (iso: string): string =>
  new Date(iso).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  });

/**
 * 「あとどれくらいか」の一文。
 *
 * 媒体の API は取り込みの総量を返さないので％は作らず、依頼からの経過時間と
 * 実測ベースの目安（estimated_seconds）を比べて言い切る。
 */
const describeRemaining = (sync: TaapInitialSync): string => {
  if (!sync.requested_at || !sync.estimated_seconds) return '';
  const elapsed = (Date.now() - new Date(sync.requested_at).getTime()) / 1000;
  const remaining = sync.estimated_seconds - elapsed;
  if (remaining <= 0) return '通常より時間がかかっています。このままお待ちください。';
  return `目安: あと${Math.ceil(remaining / 60)}分以内。`;
};

export const isInitialSyncRunning = (status: TaapSyncStatus | null): boolean =>
  status?.initial_sync?.phase === 'queued' || status?.initial_sync?.phase === 'importing';

/**
 * 数値の信頼状態を出すバー。
 *
 * 空のダッシュボードには「未接続」「同期未実行」「同期失敗」の3つの原因が
 * あり、グラフが空なだけでは利用者は区別できない。ここで必ず言い切る。
 */
const TaapSyncBar: React.FC<Props> = ({ status, loading, onConnect }) => {
  if (loading || !status) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm text-gray-500">
        <Clock className="h-4 w-4 animate-pulse" />
        接続状態を確認しています…
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3">
        <div className="flex items-center gap-2 text-sm text-amber-900">
          <Plug className="h-4 w-4" />
          広告アカウントが接続されていません。
        </div>
        <button
          type="button"
          onClick={onConnect}
          className="rounded-lg bg-amber-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
        >
          メディア連携設定へ
        </button>
      </div>
    );
  }

  if (status.has_error) {
    const failing = status.accounts.filter((account) => account.last_sync_error);

    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-destructive/20 bg-destructive/10 px-5 py-3">
        <div className="flex items-start gap-2 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">
              {failing.length}件のアカウントで同期に失敗しています。表示中の数値は最後に成功した時点のものです。
            </div>
            <div className="mt-0.5 text-xs text-red-700">
              {failing[0]?.account_name}: {failing[0]?.last_sync_error}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onConnect}
          className="rounded-lg border border-destructive/40 bg-white px-4 py-1.5 text-sm font-medium text-red-700 hover:bg-destructive/10"
        >
          接続を確認する
        </button>
      </div>
    );
  }

  const initialSync = status.initial_sync ?? null;
  const accountsLabel =
    initialSync && initialSync.pending_count > 1 ? `${initialSync.pending_count}アカウントの` : '';

  if (!status.last_synced_at && initialSync && initialSync.phase !== 'scheduled') {
    return (
      <div
        role="status"
        className="rounded-xl border border-blue-200 bg-blue-50 px-5 py-3 text-sm text-blue-900"
      >
        <div className="flex items-center gap-2">
          <TaskySpinner size="sm" role="presentation" aria-hidden className="shrink-0" />
          <span className="font-medium">
            {initialSync.phase === 'importing'
              ? `${accountsLabel}実績を取り込んでいます…`
              : `${accountsLabel}実績の取り込みを開始しています…`}
          </span>
        </div>
        {describeRemaining(initialSync) && (
          <div className="mt-1 pl-6 text-xs text-blue-800">
            {describeRemaining(initialSync)}
          </div>
        )}
      </div>
    );
  }

  if (!status.last_synced_at && initialSync?.next_scheduled_at) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm text-gray-600">
        <Clock className="h-4 w-4 shrink-0 text-gray-400" />
        次回の自動取り込み（{formatScheduledTime(initialSync.next_scheduled_at)} 頃）の後に数値が表示されます。
      </div>
    );
  }

  if (!status.last_synced_at) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm text-gray-600">
        <Clock className="h-4 w-4 text-gray-400" />
        初回の取り込み後に数値が表示されます。
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm text-gray-600">
      <span className="flex items-center gap-2 text-gray-800">
        <CheckCircle2 className="h-4 w-4 text-success" />
        {status.account_count}アカウント接続中
      </span>
      <span className="text-gray-400">·</span>
      <span>{status.platforms.map((platform) => PLATFORM_LABELS[platform] ?? platform).join(' / ')}</span>
      <span className="text-gray-400">·</span>
      <span>最終同期: {formatRelativeTime(status.last_synced_at)}</span>
      {isInitialSyncRunning(status) && (
        <>
          <span className="text-gray-400">·</span>
          <span className="flex items-center gap-1 text-blue-700">
            <TaskySpinner size="sm" role="presentation" aria-hidden />
            追加したアカウントを取り込み中
          </span>
        </>
      )}
    </div>
  );
};

export default React.memo(TaapSyncBar);
